import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EsecutoreField } from "./EsecutoreField";
import { RipartizioniEditor, newRip, ripEccede } from "./RipartizioniEditor";
import { TIPO_LABEL } from "./labels";

const nz = (v) => (v === "" || v === undefined ? null : v);
const str = (v) => (v === null || v === undefined ? "" : String(v));
const oggi = () => new Date().toISOString().slice(0, 10);

const fromReg = (r) => ({
  data: r.data, descrizione: r.descrizione, codice: str(r.codice), fornitore_id: r.fornitore_id, operaio_id: r.operaio_id,
  subappaltatore_id: r.subappaltatore_id, dipendente_id: r.dipendente_id, esecutore_nome: r.esecutore_nome, ordine_id: str(r.ordine_id),
  um: str(r.um), quantita: str(r.quantita), prezzo_unitario: str(r.prezzo_unitario), ore: str(r.ore), costo_orario: str(r.costo_orario),
  documento: str(r.documento), note: str(r.note),
});
const vuoto = (desc) => ({ data: oggi(), descrizione: desc || "", codice: "", esecutore_nome: "", ordine_id: "", um: "", quantita: "", prezzo_unitario: "", ore: "", costo_orario: "", documento: "", note: "" });

const F = ({ label, children, className = "" }) => <div className={className}><Label className="text-xs">{label}</Label>{children}</div>;

export const RegistrazioneDialog = ({ open, onOpenChange, cantiereId, computoId, tipo, initial, preset, onSaved }) => {
  const mdo = tipo === "MANODOPERA";
  const [form, setForm] = useState(initial ? fromReg(initial) : vuoto(preset?.descrizione));
  const [rips, setRips] = useState(initial ? initial.ripartizioni.map((x) => ({ ...newRip(x.riga_computo_id, str(x.voce_analisi_risorsa_id)), id: x.id, quantita: str(x.quantita), ore: str(x.ore), prezzo_unitario: initial.tipo === "MANODOPERA" && x.prezzo_origine === "MANUALE_QUOTA" ? str(x.prezzo_unitario) : "" }))
    : preset?.rigaId ? [newRip(preset.rigaId, preset.risorsaId || "")] : []);
  const [voci, setVoci] = useState([]);
  const [ordini, setOrdini] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (p) => setForm((f) => ({ ...f, ...p }));
  useEffect(() => { api.get(`/computi/${computoId}/registro-effettivo/voci`).then((r) => setVoci(r.data)); }, [computoId]);
  useEffect(() => { if (tipo === "MATERIALE") api.get(`/cantieri/${cantiereId}/ordini`).then((r) => setOrdini(r.data)); }, [tipo, cantiereId]);
  const save = async () => {
    setSaving(true);
    const body = {
      ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, nz(v)])),
      ripartizioni: rips.filter((r) => r.riga_computo_id).map((r) => ({ id: r.id || null, riga_computo_id: r.riga_computo_id, voce_analisi_risorsa_id: nz(r.voce_analisi_risorsa_id), quantita: nz(r.quantita), ore: nz(r.ore), prezzo_unitario: nz(r.prezzo_unitario) })),
    };
    try {
      const { data } = initial ? await api.patch(`/registro-effettivo/${initial.id}`, { ...body, expected_updated_at: initial.updated_at })
        : await api.post(`/cantieri/${cantiereId}/registro-effettivo`, { ...body, computo_id: computoId, tipo });
      toast.success("Registrazione salvata");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Dati non validi: controllare i campi");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="registrazione-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? "Modifica" : "Nuova"} registrazione effettiva · {TIPO_LABEL[tipo]}</DialogTitle>
          <DialogDescription>{tipo === "MATERIALE" ? "Registra la consegna: solo le quantità utilizzate sulle voci diventano costo; il resto rimane in giacenza." : mdo ? "Registra ore lavorate e/o quantità eseguite: per voce ed esecutore conta solo il criterio economico scelto." : "Registra il nolo e ripartiscilo sulle voci."} L'analisi prevista non viene modificata.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <F label="Data"><Input type="date" value={form.data} onChange={(e) => set({ data: e.target.value })} data-testid="reg-data" /></F>
            <F label="Descrizione" className="col-span-2 sm:col-span-2"><Input value={form.descrizione} onChange={(e) => set({ descrizione: e.target.value })} data-testid="reg-descrizione" /></F>
            <F label="Codice"><Input value={form.codice} onChange={(e) => set({ codice: e.target.value })} data-testid="reg-codice" /></F>
          </div>
          <EsecutoreField tipo={tipo} form={form} set={set} />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {mdo && <F label="Ore lavorate"><Input type="number" step="any" min="0" value={form.ore} onChange={(e) => set({ ore: e.target.value })} data-testid="reg-ore" /></F>}
            {mdo && <F label="Costo orario €"><Input type="number" step="any" min="0" value={form.costo_orario} onChange={(e) => set({ costo_orario: e.target.value })} data-testid="reg-costo-orario" /></F>}
            <F label={tipo === "MATERIALE" ? "Quantità consegnata" : mdo ? "Quantità eseguita (facolt.)" : "Quantità"}><Input type="number" step="any" min="0" value={form.quantita} onChange={(e) => set({ quantita: e.target.value })} data-testid="reg-quantita" /></F>
            <F label="U.M."><Input value={form.um} onChange={(e) => set({ um: e.target.value })} data-testid="reg-um" /></F>
            <F label={mdo ? "Prezzo a misura € (facolt.)" : "Prezzo unitario €"}><Input type="number" step="any" min="0" value={form.prezzo_unitario} onChange={(e) => set({ prezzo_unitario: e.target.value })} data-testid="reg-prezzo" /></F>
            <F label={tipo === "MATERIALE" ? "DDT / fattura" : "Documento"}><Input value={form.documento} onChange={(e) => set({ documento: e.target.value })} data-testid="reg-documento" /></F>
            {tipo === "MATERIALE" && (
              <F label="Ordine collegato (ordinato)" className="col-span-2">
                <select className="h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm" value={form.ordine_id} onChange={(e) => set({ ordine_id: e.target.value })} data-testid="reg-ordine-select">
                  <option value="">— Nessun ordine —</option>
                  {ordini.map((o) => <option key={o.id} value={o.id}>{o.numero_ordine || "s.n."} · {o.descrizione_materiale} · {o.quantita} {o.unita_misura || ""}</option>)}
                </select>
              </F>
            )}
          </div>
          <RipartizioniEditor tipo={tipo} voci={voci} rips={rips} setRips={setRips} form={form} />
          <F label="Note"><Textarea rows={2} value={form.note} onChange={(e) => set({ note: e.target.value })} data-testid="reg-note" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reg-annulla-btn">Chiudi</Button>
          <Button onClick={save} disabled={saving || !form.descrizione.trim() || ripEccede(rips, form)} data-testid="reg-salva-btn">{saving ? "Salvataggio…" : "Salva registrazione"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
